import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback, Suspense, useRef, useMemo } from "react";
import { ISubHeaderProps, ModuleType } from "./interface";
import { Avatar, Button, PlusCircleFilled, Tag, Tooltip } from "src/assets/imports/antdComponents";
import { getInitials, isNotesLikeStudy } from "src/pages/Studies/transformer";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/store";
import { confirmationTypes, FeatureKeysType, userAccessType } from "src/pages/Studies/models/interfaces";
import { useLocation, useNavigate } from "react-router-dom";
import TagsModal from "src/pages/Studies/components/TagsModal";
import AddCollaboratorModal from "src/pages/Studies/components/AddCollaborator";
import NotesFilterPane from "src/pages/NotesInsights/components/NotesFilterPane";
import ReportsTopBarActions from "src/pages/Reports/components/ReportsTopBarActions";
import { setConfirmationData, setUnsavedChanges } from "src/pages/ManageStudies/states/slice/unsavedChangesSlice";
import { truncateText } from "src/pages/ManageStudies/transformer";
import { hasUnsavedChangesReports } from "src/pages/Reports/transformer";
import { useFeatures } from "src/config/featureManager";
import "./style.css";

const SubHeader: React.FC<ISubHeaderProps> = ({
	title,
	subtitle,
	type = "default",
	tags = [],
	buttons = [],
	onTagSelect,
	permissions,
	updatedBy,
	createdBy,
	secondStep,
	secondStepTitle,
	handleClose,
	renderedFrom = ModuleType.Studies,
	reportIterationId,
	handleManualSave = () => {},
}) => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { isFeatureAvailable } = useFeatures();
	const textRef = useRef(null);
	const [activeTag, setActiveTag] = useState<string>("All");
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [tagList, setTagList] = useState<string[]>();
	const [fromModal, setFromModal] = useState<boolean>(false);
	const [isAddCollabModalOpen, setIsAddCollabModalOpen] = useState(false);
	const [collabModalKey, setCollabModalKey] = useState(0);
	const [isTruncated, setIsTruncated] = useState(false);

	const isStudies = title === t("Studies");
	const visibleTagCount = isStudies ? 6 : 2;
	const remainingTags = tagList?.length - visibleTagCount;
	const secondStepCondition = !!secondStep && !["Iteration"].includes(secondStep);
	const isStudyUpsert = isFeatureAvailable(FeatureKeysType.StudyUpsert);

	const { selectedStudy } = useSelector((state: RootState) => state.studiesSlice);
	const { confirmationData, hasUnsavedChanges } = useSelector((state: RootState) => state.unsavedChangesSlice);
	const { autosaveStatus, selectedReport } = useSelector((state: RootState) => state.reportsSlice);
	const { hasUnsavedSegment, isAddingSegment, isViewMode, isNewSegment: isNewSeg } = useSelector((state: RootState) => state.customSegmentSlice);
	const hasAccess = renderedFrom === ModuleType.Studies ? selectedStudy?.access_type === userAccessType.edit || selectedStudy?.access_type === userAccessType.view : true;
	const studyId = selectedStudy?.study_id;


	const unsavedChanges = useMemo((): boolean => {
		if (renderedFrom === ModuleType.Reports) {
			return hasUnsavedChangesReports(autosaveStatus);
		}
		if (renderedFrom === ModuleType.Segments) {
			return hasUnsavedSegment || isAddingSegment || isNewSeg || !isViewMode;
		}
		return hasUnsavedChanges;
	}, [renderedFrom, autosaveStatus, hasUnsavedSegment, isAddingSegment, isNewSeg, isViewMode, hasUnsavedChanges]);

	useEffect(() => {
		if (type === "button" && tags?.length > 0 && !tags.includes("All")) {
			tags = ["All", ...tags];
		}

		let updatedTags = tags;
		if (fromModal) {
			const remainingTags = updatedTags.filter((tag) => tag !== "All" && tag !== activeTag);
			if (activeTag === "All") {
				updatedTags = [activeTag, ...remainingTags];
			} else {
				updatedTags = ["All", activeTag, ...remainingTags];
			}

			if (updatedTags.includes(activeTag)) {
				updatedTags = updatedTags.filter((tag) => tag !== activeTag);
				if (updatedTags?.length >= 5) {
					updatedTags.splice(5, 0, activeTag);
				} else {
					updatedTags.push(activeTag);
				}
			}
		}

		if (JSON.stringify(updatedTags) !== JSON.stringify(tagList)) {
			setTagList(updatedTags);
		}
	}, [tags, fromModal, activeTag, type, tagList]);

	const handleTagClick = useCallback(
		(tag: string, isModalClicked: boolean) => {
			setFromModal(isModalClicked);
			setActiveTag(tag);
			onTagSelect(tag);
		},
		[onTagSelect]
	);

	const showAddCollabModal = () => {
		setCollabModalKey((prevKey) => prevKey + 1);
		setIsAddCollabModalOpen(true);
	};

	const handleOk = () => {
		setIsAddCollabModalOpen(false);
	};

	const handleCancel = () => {
		setIsAddCollabModalOpen(false);
	};

	const checkIsTruncated = () => {
		if (textRef.current) {
			const element = textRef.current;
			return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
		}
		return false;
	};

	useEffect(() => {
		const handleResize = () => {
			setIsTruncated(checkIsTruncated());
		};

		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [subtitle, checkIsTruncated]);

	const handleBackClick = () => {
		if (unsavedChanges && renderedFrom === ModuleType.Reports) {
			const confirmationData = {
				redirectFrom: confirmationTypes.type5,
				status: "warning",
				editType: "",
				visible: true,
				name: selectedReport?.name,
			};
			dispatch(setConfirmationData({ ...confirmationData }));
			return;
		}
		if (secondStepCondition && handleClose) {
			handleClose();
			return;
		}
		dispatch(setUnsavedChanges(false));
		dispatch(setConfirmationData({ ...confirmationData, visible: false }));
		if (location.pathname === "/manage-studies") {
			navigate("/studies");
		} else {
			window.history.back();
		}
	};

	const memoizedCollabContent = useMemo(() => {
		return (
			<div className="flex items-center -space-x-2 mr-2">
				<Avatar.Group size="small" className="mix-blend-normal -z-0" max={{ count: 2 }}>
					{permissions?.collaborators?.map((collaborator) => (
						<Tooltip key={collaborator.collaborator_id} title={collaborator.collaborator_name}>
							<Avatar
								key={collaborator.collaborator_id}
								className={`${collaborator.is_owner ? "owner-avatar" : ""} ${collaborator.is_active === false ? "inactive-avatar" : ""}`}>
								{getInitials(collaborator.collaborator_name)}
							</Avatar>
						</Tooltip>
					))}
				</Avatar.Group>
				<PlusCircleFilled
					className={`mix-blend-normal text-[20px] z-0 ${hasAccess ? "plus-icon" : "plus-icon-disabled"}`}
					onClick={() => {
						if (hasAccess) showAddCollabModal();
					}}
					tabIndex={0}
					role="button"
					onKeyDown={(e) => {
						if (e.key === "Enter" && hasAccess) {
							e.preventDefault();
							showAddCollabModal();
						}
					}}
				/>
			</div>
		);
	}, [permissions, hasAccess, showAddCollabModal, t]);

	return (
		<div className={`subHeader-nav flex items-center justify-between p-1 px-8 w-full`}>
			<TagsModal topTags={tagList} handleTagClick={handleTagClick} activeTag={activeTag} isModalVisible={isModalVisible} setIsModalVisible={setIsModalVisible} type={type} />
			<div className={`flex items-center gap-2 ${location.pathname === "/insights" && isNotesLikeStudy(selectedStudy?.study_type) ? "w-1/5" : ""}`}>
				<button className={`flex items-center`} onClick={handleBackClick}>
					<i className="material-symbols-outlined secondary-color text-2xl">arrow_back</i>
				</button>
				<Tooltip title={unsavedChanges ? t("Unsaved changes, only back operation allowed") : null} placement="bottomLeft">
					<button
						className={unsavedChanges ? "cursor-not-allowed" : ""}
						onClick={() => {
							if (unsavedChanges) return;
							dispatch(setUnsavedChanges(false));
							renderedFrom === ModuleType.Reports ? navigate("/reports") : navigate("/");
						}}
						disabled={unsavedChanges}>
						<i className="material-symbols-outlined filled-icon text-2xl">home</i>
					</button>
				</Tooltip>

				<i className="material-symbols-outlined text-2xl">chevron_right</i>

				<div className="flex items-center font-normal text-[--neutral-grey] space-x-1">
					<span className="underline">{title}</span>
					{subtitle && <span>-</span>}
					{typeof subtitle === "string" ? (
						<Tooltip title={subtitle?.length > 48 ? subtitle : ""}>
							<span className="whitespace-nowrap" ref={textRef}>
								{truncateText(subtitle, 48)}
							</span>
						</Tooltip>
					) : (
						subtitle
					)}
					{secondStep && (
						<>
							<i className="material-symbols-outlined text-2xl secondary-color">chevron_right</i>
							<span className="underline whitespace-nowrap">{secondStep}</span>
							{secondStepTitle && <span>-</span>}
							<Tooltip title={secondStepTitle?.length > 48 ? secondStepTitle : ""}>
								<span ref={textRef}>{truncateText(secondStepTitle, 48)}</span>
							</Tooltip>
						</>
					)}
				</div>

				{location.pathname !== "/insights" && (
					<>
						<div className="flex items-center ">
							{tagList?.slice(0, visibleTagCount).map((tag, index) => (
								<Tag
									key={`${tag}-${index}`}
									className={`${isStudies ? "subHeader-tag text-base-1" : "study-tag"} ${activeTag === tag ? "active" : ""}`}
									onClick={isStudies ? () => handleTagClick(tag, false) : undefined}>
									{t(tag)}
								</Tag>
							))}
							{remainingTags > 0 &&
								(isStudies ? (
									<Tag onClick={() => setIsModalVisible(true)} className="subHeader-tag text-base-1 cursor-pointer">
										+ {remainingTags} {t("more")}
									</Tag>
								) : (
									<Tooltip
										title={
											<div>
												{tagList?.slice(visibleTagCount).map((tag, index) => (
													<Tag key={`${tag}-${index}`} className="study-tag">
														{tag}
													</Tag>
												))}
											</div>
										}>
										<Tag className="study-tag mr-0">
											+ {remainingTags} {t("more")}
										</Tag>
									</Tooltip>
								))}
						</div>
						{type === "default" && renderedFrom === ModuleType.Studies && studyId && memoizedCollabContent}
					</>
				)}
			</div>

			{location.pathname === "/insights" && selectedStudy?.insights_available && isNotesLikeStudy(selectedStudy?.study_type) && (
				<div className="w-[60%] ml-auto">
					<NotesFilterPane />
				</div>
			)}

			<div className="flex items-center space-x-2 gap-3">
				{updatedBy && !isNotesLikeStudy(selectedStudy?.study_type) && <div className="text-[x-small] text-[--neutral-grey] font-normal">{updatedBy}</div>}
				{renderedFrom === ModuleType.Reports && location.pathname === "/add-report" && (
					<ReportsTopBarActions collabContent={memoizedCollabContent} handleManualSave={handleManualSave} />
				)}
				{buttons.map((button, index) => (
					<span key={index}>{button}</span>
				))}
				{isStudyUpsert && location.pathname === "/insights" && (
					<>
						<span className="border-l-2 border-[--secondary-color-opacity-25] h-6 mx-4" />
						<Button
							className="flex items-center"
							type="default"
							onClick={() => navigate("/manage-studies")}
							icon={<i className="material-symbols-outlined text-2xl">page_info</i>}>
							{t("Manage")}
						</Button>
					</>
				)}
			</div>
			<Suspense fallback={<></>}>
				{isAddCollabModalOpen && (
					<AddCollaboratorModal
						authorUser={createdBy}
						fullAccessUsers={permissions?.collaborators}
						isModalOpen={isAddCollabModalOpen}
						handleOk={handleOk}
						handleCancel={handleCancel}
						key={collabModalKey}
						studyId={studyId}
						studyName={""}
						renderedFrom={renderedFrom}
						reportIterationId={reportIterationId}
						reportStatus={selectedReport?.status}
					/>
				)}
			</Suspense>
		</div>
	);
};

export default React.memo(SubHeader);
